import { dateTime } from '@/_shared/utils/date-time';

/**
 * Calculate the termination date by adding the required notice period to the notice date
 * @param noticeDate - Date when notice of termination was issued (YYYY-MM-DD format)
 * @param requiredPeriodDays - Number of days for the notice period
 * @returns Calculated termination date in YYYY-MM-DD format, or null if inputs are invalid
 */
export function calculateTerminationDate(
  noticeDate: string | null | undefined,
  requiredPeriodDays: number | null | undefined
): string | null {
  if (!noticeDate || requiredPeriodDays == null || requiredPeriodDays < 0) {
    return null;
  }

  try {
    const calculatedDate = dateTime.add(noticeDate, requiredPeriodDays, 'days');
    return dateTime.format(calculatedDate, 'YYYY-MM-DD');
  } catch (error) {
    console.error('Error calculating termination date:', error);
    return null;
  }
}

/**
 * Calculate the final termination date considering override and extension
 * Priority: override date takes precedence, otherwise apply extension to calculated date
 * @param calculatedDate - Auto-calculated termination date
 * @param overrideDate - Manual override date (takes precedence)
 * @param extensionDays - Additional days to extend the termination
 * @returns Final termination date in YYYY-MM-DD format, or null if no valid date
 */
export function calculateFinalTerminationDate(
  calculatedDate: string | null | undefined,
  overrideDate: string | null | undefined,
  extensionDays: number | null | undefined
): string | null {
  // If there's an override date, use it and apply extension if provided
  if (overrideDate) {
    if (extensionDays != null && extensionDays > 0) {
      try {
        const extendedDate = dateTime.add(overrideDate, extensionDays, 'days');
        return dateTime.format(extendedDate, 'YYYY-MM-DD');
      } catch (error) {
        console.error('Error applying extension to override date:', error);
        return overrideDate;
      }
    }
    return overrideDate;
  }

  // No override, so use calculated date with extension if provided
  if (calculatedDate) {
    if (extensionDays != null && extensionDays > 0) {
      try {
        const extendedDate = dateTime.add(calculatedDate, extensionDays, 'days');
        return dateTime.format(extendedDate, 'YYYY-MM-DD');
      } catch (error) {
        console.error('Error applying extension to calculated date:', error);
        return calculatedDate;
      }
    }
    return calculatedDate;
  }

  return null;
}
